import { KnownId } from "@fairfleet/geotab";
import { LiveDataRow } from "../models/LiveDataRow";
import { normalizeUnitOfMeasure } from "./checklistValues";

/**
 * Recognising a diagnostic means looking at both its id and its name.
 *
 * The id is stable but opaque, and a database can carry custom diagnostics whose ids say
 * nothing; the name is descriptive but arrives in the language the database is set up in, which
 * is why the patterns below match German and English alike.
 */
export function haystack(row: LiveDataRow): string {
  return `${row.diagnosticId} ${row.diagnosticName}`;
}

/**
 * One of Geotab's own enum ids, as opposed to an id a database generated for itself.
 *
 * Geotab's read as words and begin with their type: `DiagnosticCanProtocolId`. A database's own
 * are random and begin with a lower-case letter: `aTWDkdT3qWEy_M_tENiZcdg`.
 */
const ENUM_ID = /^[A-Z][A-Za-z]*Id$/;

/**
 * The reading's text with the words of a PascalCase name separated.
 *
 * Short names like "CAN" and "FMS" have to be matched on word boundaries, or "cancel" and
 * "confirms" answer the check. But an id writes its words without separators, so the boundaries
 * have to be put back first: "DiagnosticCanProtocolId" becomes "Diagnostic Can Protocol Id".
 *
 * A database's own ids are left out entirely. Splitting one of those at its case changes turns
 * random characters into words -- `…xCanY…` becomes `… x Can Y …` -- and a vehicle would gain a
 * CAN protocol, and with it a passing status, by pure chance in an identifier.
 */
function spacedHaystack(row: LiveDataRow): string {
  const id = ENUM_ID.test(row.diagnosticId) ? spacedWords(row.diagnosticId) : "";
  return `${id} ${spacedWords(row.diagnosticName)}`;
}

function spacedWords(text: string): string {
  return text.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2").replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

export function isCanRow(row: LiveDataRow): boolean {
  return /\bcan\b/i.test(spacedHaystack(row));
}

const RAW_ODOMETER = /raw[\s_-]*odometer|rohkilometerstand/i;
const ODOMETER = /odometer|mileage|kilometerstand/i;

export function isRawOdometerRow(row: LiveDataRow): boolean {
  return RAW_ODOMETER.test(haystack(row));
}

/**
 * The odometer the vehicle itself reports, never the raw one.
 *
 * Nearly every vehicle reports both, they are not the same number, and the raw one carries the
 * word "odometer" in its name — so it has to be excluded explicitly or it answers both checks.
 */
export function isOdometerRow(row: LiveDataRow): boolean {
  const text = haystack(row);
  return ODOMETER.test(text) && !RAW_ODOMETER.test(text);
}

/**
 * A reading that states the FMS version.
 *
 * "CAN FMS" is a bus protocol rather than a version: it carries the word but belongs to the CAN
 * protocol list, and letting it through would show a protocol's value as the FMS version.
 */
export function isFmsVersionRow(row: LiveDataRow): boolean {
  // The spaced text is built once and both patterns run against it, rather than calling isCanRow
  // and having it build the same string a second time.
  const text = spacedHaystack(row);
  return /\bfms\b/i.test(text) && !/\bcan\b/i.test(text);
}

/**
 * The two tachograph lines, addressed by the short names an installer knows them by.
 *
 * Matched on the name and on word boundaries, never on the id: Geotab ids are random strings and
 * "d8" turns up inside them by chance often enough that an unrelated reading would answer the
 * check -- with a number that looks like a real tachograph value.
 */
export function isTachoCanCRow(row: LiveDataRow): boolean {
  return /\bcanc\b/i.test(row.diagnosticName);
}

export function isTachoD8Row(row: LiveDataRow): boolean {
  return /\bd8\b/i.test(row.diagnosticName);
}

function isFuelLevelRow(row: LiveDataRow): boolean {
  return /fuel.?level/i.test(haystack(row));
}

/**
 * The tank level as a percentage, and as a volume.
 *
 * Two checks off one diagnostic, told apart by the unit: a vehicle can report either or both, and
 * which ones arrive is exactly what an installer is verifying. Composed here rather than at each
 * page, so the single-vehicle checklist and the fleet-wide view cannot answer them differently.
 */
export function isFuelLevelPercentRow(row: LiveDataRow): boolean {
  return isFuelLevelRow(row) && isPercentOrUnknownUnit(row.unitOfMeasure);
}

export function isFuelLevelVolumeRow(row: LiveDataRow): boolean {
  return isFuelLevelRow(row) && isVolumeUnit(row.unitOfMeasure);
}

/**
 * Whether a tank reading is a percentage — including when no unit was reported at all.
 *
 * A tank level without a unit is a percentage far more often than a volume, and treating the
 * unknown case as a volume instead would silently drop the percentage check for those vehicles.
 */
export function isPercentOrUnknownUnit(unitOfMeasure: string): boolean {
  // Normalised first, because Geotab reports the unit either as a word or as an enum id
  // (`UnitOfMeasurePercentageId`) and both have to count as a percentage.
  const normalized = normalizeUnitOfMeasure(unitOfMeasure).trim();
  return normalized === "" || normalized === "%";
}

const VOLUME_SYMBOLS = new Set(["l", "gal"]);

export function isVolumeUnit(unitOfMeasure: string): boolean {
  // Normalised first, like the percentage check, so both Geotab's enum id and the display name
  // are recognised by one rule instead of two patterns that can drift apart.
  return VOLUME_SYMBOLS.has(normalizeUnitOfMeasure(unitOfMeasure).trim().toLowerCase());
}

/**
 * Diagnostic ids observed in one customer database, for values that database defines itself.
 *
 * They match nowhere else, and the name patterns below cover the same values wherever a database
 * names them recognisably. Kept alongside so that nothing which matches today stops matching —
 * but a mapping held per database is the real answer, not literals in shared code.
 */
const CUSTOMER_IDS = {
  intelligentHarness: "aTWDkdT3qWEy_M_tENiZcdg",
  hvBatteryCurrent: "aoceAsl774UaFnkAEklMajw",
  hvBatteryVoltage: "a4kUpYGyQ4E2uoQTp-ed0Ow",
};

interface DiagnosticRule {
  /**
   * Geotab's own ids for this value, where it standardises one.
   *
   * Checked as whole ids rather than as substrings, and taken from `KnownId` so they cannot be
   * misspelled — there is no `DiagnosticSpeedId`, and a typed-out literal for it matches nothing
   * while looking entirely plausible.
   */
  ids?: string[];
  /**
   * How the value is named, in the languages a database may be set up in.
   *
   * The fallback for everything Geotab does not standardise, and the only handle for a database
   * that carries the value as a custom diagnostic.
   */
  pattern?: RegExp;
}

/**
 * How each checklist value is recognised.
 *
 * All of it in one place, next to the units and the special cases below, because the fleet-wide
 * view asks the same questions of the same readings and must not have to copy the rules.
 */
const RULES = {
  intelligentHarness: {
    ids: [CUSTOMER_IDS.intelligentHarness],
    pattern:
      /intelligent\w*[\s-]*(?:harness|kabelbaum|kabelstrang)|(?:harness|kabelbaum|kabelstrang)\w*[\s-]*intelligent/i,
  },
  standardHarness: {
    pattern:
      /standard\w*[\s-]*(?:harness|kabelbaum|kabelstrang)|(?:harness|kabelbaum|kabelstrang)\w*[\s-]*standard/i,
  },
  deviceVoltage: {
    ids: [KnownId.DiagnosticGoDeviceVoltageId],
    pattern: /device.?voltage|telematik.?spannung|ger(?:ä|ae)tespannung/i,
  },
  ignition: {
    ids: [KnownId.DiagnosticIgnitionId],
    pattern: /ignition|z(?:ü|ue)ndung/i,
  },
  vehicleSpeed: {
    ids: [KnownId.DiagnosticEngineRoadSpeedId],
    pattern: /vehicle.?speed|fahrzeuggeschwindigkeit/i,
  },
  fmsStandardSoftwareVersion: {
    pattern: /fms.?standard.?software.?version|fms-standardsoftware-version/i,
  },
  engineSpeed: {
    ids: [KnownId.DiagnosticEngineSpeedId],
    pattern: /engine.?speed|motordrehzahl|drehzahl/i,
  },
  defLevel: {
    ids: [KnownId.DiagnosticDieselExhaustFluidId],
    pattern: /adblue|def.?level|urea|harnstoff/i,
  },
  fuelConsumption: {
    pattern: /fuel.?consum|fuel.?used|fuel.?usage|fuel.?quantity|kraftstoffverbrauch|kraftstoffmenge/i,
  },
  stateOfCharge: {
    ids: [KnownId.DiagnosticStateOfChargeId],
    pattern: /state.?of.?charge|\bsoc\b|ladezustand/i,
  },
  chargingState: {
    ids: [KnownId.DiagnosticElectricVehicleChargingStateId],
    pattern: /charging.?state|ladestatus|ladevorgang/i,
  },
  hvBatteryCurrent: {
    ids: [KnownId.DiagnosticBatteryCurrentId, CUSTOMER_IDS.hvBatteryCurrent],
    pattern: /battery\s*current|batteriestrom/i,
  },
  hvBatteryVoltage: {
    ids: [KnownId.DiagnosticBatteryVoltageId, CUSTOMER_IDS.hvBatteryVoltage],
    pattern: /battery\s*voltage|batteriespannung/i,
  },
  hvBatteryPower: {
    ids: [KnownId.DiagnosticElectricVehicleBatteryPowerId],
    pattern: /battery\s*power|batterieleistung/i,
  },
  // Geotab standardises no id for the five below, so a name is the only handle.
  //
  // The range goes by several names, and Geotab's own German for it is "Verbleibende Strecke des
  // Elektrofahrzeugs" -- neither "Reichweite" nor "range" appears in that at all. A bare "range"
  // or "distance" is no use either: the first matches the Bluetooth beacon's "in range", the
  // second every odometer. So each of those needs a word beside it.
  evRange: {
    pattern:
      /(?:rest)?reichweite|(?:verbleibende|restliche)\s+strecke|reststrecke|\b(?:remaining|electric|driving|ev|battery)[\s-]*(?:range|distance)|(?:range|distance)[\s-]*(?:remaining|estimate)/i,
  },
  // "Voltage" is not the only spelling in the field: the values observed read "Battery maximum
  // cell volt", so anything starting with "volt" counts -- volt, volts, voltage alike.
  cellVoltageHigh: {
    pattern:
      /(?:zellspannung|cell[\s-]*volt)[^,;]*(?:h(?:ö|oe)chst\w*|max\w*|high)|(?:h(?:ö|oe)chst\w*|max\w*|high)[\s-]*(?:zellspannung|cell[\s-]*volt)/i,
  },
  cellVoltageLow: {
    pattern:
      /(?:zellspannung|cell[\s-]*volt)[^,;]*(?:niedrigst\w*|min\w*|low)|(?:niedrigst\w*|min\w*|low)[\s-]*(?:zellspannung|cell[\s-]*volt)/i,
  },
  cellTemperatureHigh: {
    pattern:
      /(?:zelltemperatur|cell[\s-]*temp\w*)[^,;]*(?:h(?:ö|oe)chst\w*|max\w*|high)|(?:h(?:ö|oe)chst\w*|max\w*|high)[\s-]*(?:zelltemperatur|cell[\s-]*temp\w*)/i,
  },
  cellTemperatureLow: {
    pattern:
      /(?:zelltemperatur|cell[\s-]*temp\w*)[^,;]*(?:niedrigst\w*|min\w*|low)|(?:niedrigst\w*|min\w*|low)[\s-]*(?:zelltemperatur|cell[\s-]*temp\w*)/i,
  },
} satisfies Record<string, DiagnosticRule>;

export type CheckKey = keyof typeof RULES;

/** Whether a reading is the value `key` asks about. */
export function matchesCheck(key: CheckKey, row: LiveDataRow): boolean {
  const rule: DiagnosticRule = RULES[key];
  return (
    (rule.ids?.includes(row.diagnosticId) ?? false) || (rule.pattern?.test(haystack(row)) ?? false)
  );
}
